import { HubConnection } from '@microsoft/signalr';
import type { Observable } from 'rxjs';
import { merge, NEVER } from 'rxjs';
import { distinct, share, tap } from 'rxjs/operators';
import { PriceStreamingEvent } from 'utils/streaming/quotePriceEvent';
import { SmartQuoteEvent } from 'utils/streaming/smart-quote';

import { logIncomingLightweightEvent, withPollingSource, withSignalrSource } from './events';
import type {
  BackTestEvent,
  ConnectionEvent,
  DocumentationEvent,
  LightweightEvent,
  MailStatusEvent,
  PriceImprovementEvent,
  SecondaryMarketEvent,
  SecondaryMarketPriceEvent,
  SessionSynchronizationEvent,
  Streaming,
} from './model';
import { initPolling } from './polling/init';
import { connect } from './signalr';

export function initStreaming(): Streaming {
  const sources$: Array<Observable<LightweightEvent>> = [];

  let connectionEvent$: Observable<ConnectionEvent> = NEVER;
  let sessionSynchronizationMessage$: Observable<SessionSynchronizationEvent> = NEVER;
  let priceImprovementMessage$: Observable<PriceImprovementEvent> = NEVER;
  let priceStreamingMessage$: Observable<PriceStreamingEvent> = NEVER;
  let smartQuoteMessage$: Observable<SmartQuoteEvent> = NEVER;
  let documentationMessage$: Observable<DocumentationEvent> = NEVER;
  let secondaryMarketMessage$: Observable<SecondaryMarketEvent> = NEVER;
  let secondaryMarketPriceMessage$: Observable<SecondaryMarketPriceEvent> = NEVER;
  let backTestMessage$: Observable<BackTestEvent> = NEVER;
  let mailStatusMessage$: Observable<MailStatusEvent> = NEVER;
  let connection: HubConnection | undefined;
  if (window.sgmeConfiguration.enablePolling === true) {
    const lightweight$ = initPolling(window.sgmeConfiguration.pollingIntervalMs);
    sources$.push(withPollingSource(lightweight$));
  }
  if (window.sgmeConfiguration.enableSignalr === true) {
    const {
      data$,
      sessionSynchronizationMessage$: sync$,
      priceImprovementMessage$: priceImprovement$,
      priceStreamingMessage$: quoteStreaming$,
      smartQuoteMessage$: smartQuote$,
      documentationMessage$: documentation$,
      secondaryMarketMessage$: secondaryMarket$,
      secondaryMarketPriceMessage$: secondaryMarketPrice$,
      backTestMessage$: backTest$,
      mailStatusMessage$: mailStatus$,
      metadata$,
      connection: signalRConnection,
    } = connect<LightweightEvent>(window.sgmeConfiguration.signalrHubUrl, window.sgmeConfiguration);
    sources$.push(withSignalrSource(data$));
    connectionEvent$ = metadata$;
    connection = signalRConnection;
    sessionSynchronizationMessage$ = sync$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'sessionSynchronizationMessage')),
    );
    priceImprovementMessage$ = priceImprovement$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'priceImprovementMessage')),
      share(),
    );
    secondaryMarketMessage$ = secondaryMarket$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'secondaryMarketMessage')),
      share(),
    );
    secondaryMarketPriceMessage$ = secondaryMarketPrice$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'secondaryMarketPriceMessage')),
      share(),
    );
    backTestMessage$ = backTest$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'backTestMessage')),
      share(),
    );
    priceStreamingMessage$ = quoteStreaming$.pipe(
      tap(m => logIncomingLightweightEvent(m, `priceStreamingMessage ${m.type}`)),
      share(),
    );
    smartQuoteMessage$ = smartQuote$.pipe(
      tap(m => logIncomingLightweightEvent(m, `smartQuoteMessage ${m.type}`)),
      share(),
    );
    documentationMessage$ = documentation$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'documentationMessage')),
      share(),
    );
    mailStatusMessage$ = mailStatus$.pipe(
      tap(m => logIncomingLightweightEvent(m, 'mailStatusMessage')),
      share(),
    );
  }

  const lightEvent$ = merge(...sources$, NEVER).pipe(
    distinct(event => event.eventId),
    tap(e => logIncomingLightweightEvent(e, 'lightEvent')),
    share(),
  );

  const result: Streaming = {
    lightEvent$,
    connectionEvent$,
    priceStreamingMessage$,
    documentationMessage$,
    sessionSynchronizationMessage$,
    priceImprovementMessage$,
    secondaryMarketMessage$,
    secondaryMarketPriceMessage$,
    smartQuoteMessage$,
    backTestMessage$,
    mailStatusMessage$,
    connection,
  };
  return result;
}
